// release-secrets-stdin-missing-keys-hint.ts — phase 04b (spec V2 §4.2).
//
// G3 approved mà key release trong `env_allowlist` vắng ở MỌI nguồn ⇒ hành vi
// v1 (recipe chạy với env thiếu). `run` chỉ IN THÊM một gợi ý khi kết thúc ở
// `waiting_gate:g3` (có deploy) hoặc deploy/smoke fail: tên key thiếu + lệnh
// `--release-secrets-stdin`. Chỉ tên key, không bao giờ giá trị. Không đổi
// nhãn, exit code hay journal. Gợi ý khuyên `--release-secrets-stdin`, mà chế
// độ đó bỏ hẳn `test.env` (H1) ⇒ key thiếu tính theo CHẾ ĐỘ STDIN: không có ở
// stdin lẫn shell (review vòng sửa M-a). Không stdin mà `test.env` đang cấp một
// số key đó ⇒ nêu riêng tên chúng ("test.env bị bỏ — đưa cả … vào stdin").
// `test.env` có mà không được cấp (quyền/owner) ⇒ nói rõ (L5).
import { loadMissionTestEnv } from "./mission-secrets-test-env-loader.js";
import { releaseUsableTestEnvValues } from "./recipe-env-source-merger.js";
import type { ReleaseConfig } from "./release-manifest-hash.js";
import type { ReleaseSecretsStdin } from "./release-secrets-stdin-reader.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";

export interface MissingReleaseSecretsHintInput {
  missionId: string;
  releaseFlow: ReleaseFlowResumeOutcome | undefined;
  release: ReleaseConfig | undefined;
  releaseSecretsStdin?: ReleaseSecretsStdin;
}

function hintApplies(flow: ReleaseFlowResumeOutcome | undefined): boolean {
  if (flow === undefined) return false;
  if (flow.kind === "waiting_gate_g3") return flow.deployConfigured;
  return flow.kind === "deploy_blocked" || flow.kind === "deploy_smoke_failed";
}

interface ReleaseTestEnvKeys {
  keys: Set<string>;
  /** File có mà không được cấp (quyền/owner/không đọc được). */
  withheld: boolean;
}

async function releaseTestEnvKeys(missionId: string): Promise<ReleaseTestEnvKeys> {
  try {
    const loaded = await loadMissionTestEnv(missionId);
    if (loaded.status === "ok") {
      return {
        keys: new Set(Object.keys(releaseUsableTestEnvValues(loaded.values))),
        withheld: false,
      };
    }
    return { keys: new Set(), withheld: loaded.status !== "absent" };
  } catch {
    return { keys: new Set(), withheld: true };
  }
}

export async function describeMissingReleaseSecretsHint(
  input: MissingReleaseSecretsHintInput,
): Promise<string | undefined> {
  const release = input.release;
  if (release === undefined || !hintApplies(input.releaseFlow)) return undefined;
  const wanted = new Set([
    ...(release.deploy_recipe?.env_allowlist ?? []),
    ...(release.verify_recipe?.env_allowlist ?? []),
  ]);
  const stdin = input.releaseSecretsStdin;
  // M-a: thiếu theo chế độ stdin — `test.env` không tính.
  const missing = [...wanted].filter(
    (key) => !Object.hasOwn(stdin ?? {}, key) && process.env[key] === undefined,
  );
  if (missing.length === 0) return undefined;
  const fromFile: ReleaseTestEnvKeys =
    stdin === undefined
      ? await releaseTestEnvKeys(input.missionId)
      : { keys: new Set<string>(), withheld: false };
  const fileOnly = missing.filter((key) => fromFile.keys.has(key));
  const fileNote =
    fileOnly.length > 0
      ? ` Chạy --release-secrets-stdin thì test.env bị bỏ — đưa cả key test.env đang cấp vào stdin: ${fileOnly.join(", ")}. (Không cờ: release lấy chúng từ test.env.)`
      : "";
  const withheld = fromFile.withheld
    ? " test.env không được cấp (quyền/owner) — xem `avatar autopilot doctor`."
    : "";
  return (
    `Key release trong env_allowlist không có ở stdin lẫn shell: ${missing.join(", ")}.${fileNote}${withheld} ` +
    "Secret production không ghi file — nạp qua stdin khi deploy: " +
    `<lệnh in KEY=VALUE> | avatar autopilot run --mission ${input.missionId} --release-secrets-stdin`
  );
}
